<template>
  <q-page padding>
    <div class="text-uppercase text-h6 text-center q-my-md">
      {{ $t('label.profile') }}
    </div>
    <div class="row justify-center">
      <div class="col-12 col-sm-8 col-md-6 col-lg-4">
        <q-form @submit="save">
          <div
            v-if="account"
            class="q-gutter-md"
          >
            <q-input
              v-model="currentAccount.full_name"
              autocomplete="name"
              :label="$t('label.user.full_name')"
            />
            <template v-for="field in ['email', 'phone']">
              <component
                :is="getComponent(field)"
                :key="field"
                v-model="currentAccount[field]"
                :autocomplete="field === 'email' ? 'email' : 'tel'"
                :label="$t(`label.${field}`)"
                :stack-label="!!account[field]"
              >
                <template
                  v-if="account[field]"
                  #control
                >
                  <div class="break-word">
                    {{ account[field] }}
                  </div>
                </template>
                <q-btn
                  v-if="account[field] && !isManager"
                  slot="append"
                  round
                  flat
                  icon="edit"
                  @click="contactToChange = field"
                />
              </component>
            </template>
            <q-input
              v-if="currentAccount.groups.includes('carrier/main_manager')"
              v-model="currentAccount.carrier_ref_code"
              autocomplete="off"
              :disable="!!account.carrier_ref_code"
              :label="$t('label.invitation_code')"
            />
            <q-input
              v-model="currentAccount.password"
              autocomplete="new-password"
              :type="showPassword ? 'text' : 'password'"
              :label="$t('label.password')"
              :rules="[passwordRule]"
              lazy-rules
            >
              <q-btn
                slot="append"
                round
                flat
                :icon="showPassword ? 'visibility_off' : 'visibility'"
                @click="showPassword = !showPassword"
              />
            </q-input>
            <q-input
              v-model="currentAccount.password_confirmation"
              :type="showPasswordConfirmation ? 'text' : 'password'"
              :label="$t('label.confirm_password')"
              :rules="[passwordConfirmRule]"
              lazy-rules
            >
              <q-btn
                slot="append"
                round
                flat
                :icon="showPasswordConfirmation ? 'visibility_off' : 'visibility'"
                @click="showPasswordConfirmation = !showPasswordConfirmation"
              />
            </q-input>
            <div
              v-for="(groups, key) in currentAccount.allowed_notification_groups"
              :key="key"
            >
              <div class="text-bold">
                {{ $t(`notifications.${key}`) }}
              </div>
              <div v-if="key === 'carrier'">
                <q-select
                  v-model="currentAccount.groups"
                  :options="newTransfersGroups.options"
                  :label="$t('notifications.new_transfers.common')"
                  multiple
                  emit-value
                  map-options
                >
                  <template #after-options>
                    <div class="q-ma-md">
                      <q-btn
                        v-close-popup
                        class="full-width"
                        color="positive"
                        :label="$t('button.done')"
                      />
                    </div>
                  </template>
                  <template #selected>
                    {{ newTransfersGroups.labels }}
                  </template>
                  <template #option="scope">
                    <q-item
                      v-bind="scope.itemProps"
                      v-on="scope.itemEvents"
                    >
                      <q-item-section>
                        <q-item-label>{{ scope.opt.label }}</q-item-label>
                      </q-item-section>
                      <q-item-section side>
                        <q-checkbox
                          v-model="currentAccount.groups"
                          :val="scope.opt.value"
                        />
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
                <div v-if="isCarrierManager">
                  <q-checkbox
                    v-model="currentAccount.groups"
                    val="push_notifications/carrier/new_transfers"
                    :label="$t('notifications.new_transfers.push')"
                  />
                  <q-checkbox
                    v-model="currentAccount.groups"
                    val="email_notifications/carrier/new_transfers"
                    :label="$t('notifications.new_transfers.email')"
                  />
                </div>
              </div>
              <div
                v-for="notify in groups"
                :key="notify"
              >
                <q-checkbox
                  v-if="notify.includes('/passenger')"
                  v-model="currentAccount.groups"
                  :val="notify"
                  :label="$t('notifications.new_offers')"
                />
                <q-checkbox
                  v-else-if="notify.includes('/marketing')"
                  v-model="currentAccount.groups"
                  :val="notify"
                  :label="$t('notifications.marketing')"
                />
                <q-checkbox
                  v-else-if="!notify.includes('/new_transfers')"
                  v-model="currentAccount.groups"
                  :val="notify"
                  :label="$t(`label.allowed_notification_groups.${notify}`)"
                />
              </div>
            </div>
            <q-checkbox v-model="currentAccount.terms_accepted">
              <div
                @click.stop
                v-html="$t('label.accept_terms', {
                  link: $router.resolve({
                    name: 'terms_of_use',
                  }, $route).href,
                })"
              />
            </q-checkbox>
            <div class="text-center">
              <q-btn
                color="positive"
                unelevated
                type="submit"
                :label="$t('button.save')"
                :loading="isLoading"
              />
            </div>
          </div>
        </q-form>
        <div class="text-center q-mt-md">
          <q-btn
            v-if="currentAccount.deletable"
            :label="$t('button.delete_my_account')"
            color="red"
            unelevated
            @click="deleteUser"
          />
          <template v-else>
            {{ $t('label.if_delete_account') }}
          </template>
        </div>
      </div>
    </div>

    <contact-change-dialog v-model="contactToChange" />
  </q-page>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import IntlTelInput from '~components/IntlTelInput';
import ContactChangeDialog from '~components/ContactChangeDialog';

import Dialog from '~mixins/Dialog';
import { AccountSettingsMethods } from '~mixins';

export default {
  name: 'AccountSettings',
  components: {
    IntlTelInput,
    ContactChangeDialog,
  },
  mixins: [
    Dialog,
    AccountSettingsMethods,
  ],
  data() {
    const { account } = this.$store.getters;
    return {
      contactToChange: null,
      isLoading: false,
      passRegExp: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{8,}$/,
      showPassword: false,
      showPasswordConfirmation: false,
      currentAccount: {
        ...account,
        password: '',
        password_confirmation: '',
      },
    };
  },
  computed: {
    ...mapGetters(['invitation', 'isCarrierManager']),
    newTransfersGroups() {
      const newTransfersPrefix = 'email_notifications/carrier/new_transfers/';
      const getLabel = (el) => this.$t(`transport_types.${el.replace(newTransfersPrefix, '')}.common`);
      const groups = this.currentAccount.groups.filter((el) => el.startsWith(newTransfersPrefix));
      const options = this.currentAccount
        .allowed_notification_groups.carrier?.filter((el) => el.includes('new_transfers')).map((value) => ({
          label: getLabel(value),
          value,
        })) || [];

      return {
        options,
        labels: groups.map((el) => getLabel(el)).join(', '),
      };
    },
    isManager() {
      return this.account.groups.some((el) => el.includes('manager/'));
    },
  },
  mounted() {
    if (this.invitation) {
      const {
        error,
        inviter_email,
        inviter_title,
        type,
      } = this.invitation;

      if (error) {
        this.showDialog({
          message: this.$t('invitation.outdated'),
        });
        this.$store.dispatch('invitations/removeInvitation');
        return;
      }

      this.showDialog({
        cancel: true,
        message: this.$t('invitation.join_account', {
          email: inviter_email,
          title: inviter_title && type !== 'passenger' ? `"${inviter_title}"` : '',
        }),
        persistent: true,
      })
        .onOk(async () => {
          try {
            await this.$axios.put(`/api/invitations/accept/${this.invitation.uuid}`);
          } catch ({ response }) {
            if (response.data.errors?.account.includes('exists')) {
              this.showDialog({
                message: this.$t('invitation.account_exists'),
                ok: {
                  color: 'red',
                  label: this.$t('button.log_out'),
                },
              })
                .onOk(() => this.logout('account.login'));
            }
          }
        })
        .onCancel(() => {
          this.$axios.put(`/api/invitations/reject/${this.invitation.uuid}`);
        })
        .onDismiss(() => {
          this.$store.dispatch('invitations/removeInvitation');
        });
    }
  },
  methods: {
    ...mapActions('account', ['setAccount']),
    async save() {
      this.isLoading = true;
      try {
        const { data } = (await this.$axios.put('/api/account', {
          account: this.currentAccount,
        }));
        this.currentAccount = { ...data.account };
        this.setAccount(data.account);
        this.$q.notify(this.$t('notice.record.saved'));
      } catch (e) {
        if (e.response.data.error?.type === 'account_exists') {
          window.location = e.response.data.error.details.redirect_to;
        } else {
          console.error(e);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async deleteUser() {
      this.showDialog({
        message: this.$t('confirm.delete_my_account'),
      })
        .onOk(async () => {
          this.$q.loading.show();
          try {
            await this.$axios.delete('/api/account');
            await this.$router.push({ name: 'index' });
            await this.setAccount(null);
          } catch (err) {
            console.error(err);
          } finally {
            this.$q.loading.hide();
          }
        });
    },
    passwordRule(v) {
      return v ? this.passRegExp.test(v) || this.$t('error.password.rule') : true;
    },
    passwordConfirmRule(v) {
      return this.currentAccount.password || v
        ? v === this.currentAccount.password || this.$t('error.password.confirmation')
        : true;
    },
    getComponent(field) {
      if (this.account[field]) {
        return 'q-field';
      }

      if (field === 'phone') {
        return 'intl-tel-input';
      }

      return 'q-input';
    },
  },
};
</script>
